// A number, drawn to a canvas, for carving into the green stone.
//
// It has to be dynamic: the count is everyone who has ever come to look at
// him, so it changes on its own and no pre-made image could ever keep up.
// Canvas2D is the cheap right answer here. One 1024x256 texture, redrawn
// only when the digits actually change (roughly hourly, since that is how
// often the sim polls Vercel), then handed to the GPU once.
//
// Drawn WHITE on transparent. The stone's material tints it green and the
// emissive slot makes it glow, so the same texture works lit or resting and
// the colour stays owned by one place. Do not bake the green in here.

import * as THREE from 'three/webgpu'

const W = 1024
const H = 256

// Monospace on purpose. A tally is a count, and proportional digits make a
// changing number jitter as it re-centres on every update.
const FONT = '700 150px ui-monospace, "SF Mono", Menlo, Consolas, monospace'

/**
 * @param {number} value the count to draw
 * @returns {THREE.CanvasTexture}
 */
export function makeTallyTexture(value) {
  const canvas = document.createElement('canvas')
  canvas.width = W
  canvas.height = H
  const ctx = canvas.getContext('2d')
  drawTally(ctx, value)

  const tex = new THREE.CanvasTexture(canvas)
  tex.colorSpace = THREE.SRGBColorSpace
  tex.anisotropy = 4
  // Clamped, and it matters: the shader projects this onto the rock by world
  // position, so anything outside the carved rectangle samples off the edge.
  // Repeat wrapping would tile the number across the whole stone. Clamped, the
  // transparent border pixel is what gets sampled, which masks the rectangle
  // for free and saves a branch per fragment.
  tex.wrapS = THREE.ClampToEdgeWrapping
  tex.wrapT = THREE.ClampToEdgeWrapping
  tex.needsUpdate = true
  // Keep the canvas on the texture so a redraw can reuse it rather than
  // allocating a second one every time the count ticks.
  tex.userData.canvas = canvas
  return tex
}

/** Redraw in place. Cheaper than a new texture and keeps the GPU handle. */
export function updateTallyTexture(tex, value) {
  const canvas = tex?.userData?.canvas
  if (!canvas) return
  drawTally(canvas.getContext('2d'), value)
  tex.needsUpdate = true
}

function drawTally(ctx, value) {
  ctx.clearRect(0, 0, W, H)
  const text = formatCount(value)
  // Keep the digits off the edge. The clamp masking above only works while
  // the border pixels are transparent, and a long count that ran to the edge
  // would smear its last column all the way round the rock.
  ctx.save()
  ctx.beginPath()
  ctx.rect(24, 12, W - 48, H - 24)
  ctx.clip()

  ctx.font = FONT
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'

  // Cut, not printed. A soft dark pass offset down-right reads as the
  // shadowed inner wall of a groove, and the bright pass sits above it as
  // the lit edge. Without the dark pass the digits look stuck on.
  ctx.fillStyle = 'rgba(0,0,0,0.55)'
  ctx.fillText(text, W / 2 + 5, H / 2 + 5)

  ctx.fillStyle = '#ffffff'
  ctx.fillText(text, W / 2, H / 2)
  ctx.restore()
}

/** 1204 -> "1,204". Grouped so a five figure count stays readable at size. */
export function formatCount(value) {
  const n = Math.max(0, Math.floor(Number(value) || 0))
  return n.toLocaleString('en-GB')
}
